import { TPermissionBlockProps } from '@verifime/components';
import { formatToDDMMMYYYY } from '@verifime/utils';
import { permissionsForOrganisation } from '../Permission/currentPermission';
import { useAPIPermissions } from '../Permission/hooks';
import { useEffect, useMemo } from 'react';

export type TUseCustomerPermissionParams = {
  customerId?: string;
  /**
   * Which organisation's permission to show. **Required, not optional.**
   *
   * Load-bearing rather than a filter of convenience: the endpoint filters by customer only, so a
   * customer onboarded to two clients returns two rows, and rendering the wrong one would show a
   * client a relationship the customer has with someone else.
   *
   * It was optional in review and that was a defect. This repo builds with `strictNullChecks: false`
   * so omitting it raised nothing, and `organisationId` is optional on the response too, so a caller
   * who omitted it plus a record missing it matched `undefined === undefined` and disclosed another
   * organisation's permission. Required here, and guarded below, so neither half can happen.
   */
  organisationId: string;
  onError?: (error: unknown) => void;
};

export type TUseCustomerPermission = {
  status: 'loading' | 'error' | 'ready';
  /**
   * Undefined when this organisation holds no permission for the customer, and while loading or
   * after a failure.
   *
   * The card drops the field entirely rather than rendering it empty, because holding no permission
   * record and holding a blank one are different things and only the first can be true.
   */
  permission?: TPermissionBlockProps;
};

/**
 * Turns one permission record into the two shapes the card can render.
 *
 * A revoked permission carries its revocation date **instead of** its grant date, not alongside it:
 * a reader shown both has to work out which one is current, and that is exactly the confusion the
 * discriminated union on the component exists to prevent.
 */
function toPermissionBlock(record: {
  addedDate?: string;
  removedDate?: string;
  permissionStatus?: string;
}): TPermissionBlockProps {
  // Reads the API's own status field rather than inferring from the presence of a revocation date.
  // A record that is Inactive with no removedDate recorded would otherwise render as Active, which
  // is a false statement about a client's relationship with a customer on a compliance surface.
  // `removedDate` is the fallback only when the status field is absent.
  const isInactive = record.permissionStatus
    ? record.permissionStatus === 'Inactive'
    : Boolean(record.removedDate);

  return isInactive
    ? { status: 'inactive', revokedOn: formatToDDMMMYYYY(record.removedDate) }
    : { status: 'active', grantedOn: formatToDDMMMYYYY(record.addedDate) };
}

/**
 * Reads the customer's standing permission for one organisation.
 *
 * Formatting happens here rather than in the card, so the card renders exactly what it is given and
 * a page cannot end up with two date formats by reaching for two different helpers.
 *
 * **The read goes through `useAPIPermissions`, shared with the biometric request gate.** Both
 * surfaces render on the same page and need the same rows; subscribing to one react-query key means
 * the page asks once, and — the part that matters more — means the Details field and the card's
 * control cannot disagree about a customer's permission because one of two separate requests
 * happened to fail. Asking once is a requirement of VME-4156, not an optimisation.
 */
export function useCustomerPermission({
  customerId,
  organisationId,
  onError,
}: TUseCustomerPermissionParams): TUseCustomerPermission {
  // Both ids are required to identify a permission. Without the organisation guard, a record whose
  // own organisationId is absent would match an undefined one and be rendered.
  const enabled = Boolean(customerId && organisationId);

  const { permissions, isLoadingPermissions, loadPermissionsError } = useAPIPermissions(
    customerId ?? '',
    { enabled },
  );

  useEffect(() => {
    if (loadPermissionsError) {
      onError?.(loadPermissionsError);
    }
    // `onError` is left out on purpose: a caller passing an inline arrow would otherwise re-report
    // the same failure on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPermissionsError]);

  return useMemo(() => {
    // A query held back by `enabled` still reports `isLoadingPermissions: true`, so the precondition
    // is read first. Loading is the right answer here either way — the card shows a placeholder
    // rather than asserting that no permission exists.
    if (!enabled || isLoadingPermissions) {
      return { status: 'loading' };
    }

    // Before the rows are read, not after. A failed query carries no data and the shared hook
    // renders no data as `[]`, which would present as "this organisation holds no permission" — a
    // statement about a client's relationship with a customer that the response never made.
    if (loadPermissionsError) {
      return { status: 'error' };
    }

    // Newest first, not "whichever the server listed first". The endpoint returns every row
    // for the customer in no order (`PermissionPersistenceService.java:82-87` is
    // `find(...).list()` with no `Sort`), and revocation updates a row rather than appending
    // one, so a customer who granted, revoked and granted again has two rows here and only the
    // newest describes today. Reading an arbitrary one is the VME-4158 production defect
    // verbatim: filtered but unordered, it hit an older revoked row and reported a re-granted
    // permission as inactive. Shared with the request gate (`currentPermission.ts`) so the
    // Details field and the card's control can never disagree about which row is current.
    const [mine] = permissionsForOrganisation(permissions, organisationId);

    return { status: 'ready', permission: mine ? toPermissionBlock(mine) : undefined };
  }, [enabled, isLoadingPermissions, loadPermissionsError, permissions, organisationId]);
}
